import React, { useEffect, useMemo, useState } from 'react';
import { Megaphone, RefreshCw, Loader, Calendar, ExternalLink, CheckCircle, XCircle, AlertTriangle, Search } from 'lucide-react';
import clsx from 'clsx';
import { CampaignService, Campaign, CampaignItem, CampaignMarketplace, JoinResult } from '../services/campaignService';

const brl = (v?: number | null) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const data = (d?: string | null) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const CAMP_STATUS: Record<string, { label: string; cls: string }> = {
  pending:  { label: 'Agendada',  cls: 'text-sky-700 bg-sky-50 border-sky-300' },
  started:  { label: 'Ativa',     cls: 'text-emerald-700 bg-emerald-50 border-emerald-300' },
  finished: { label: 'Encerrada', cls: 'text-dark-400 bg-dark-700 border-dark-600' },
};

const ITEM_STATUS: Record<string, { label: string; cls: string }> = {
  candidate: { label: 'Elegível',  cls: 'text-amber-700 bg-amber-50 border-amber-300' },
  pending:   { label: 'Agendado',  cls: 'text-sky-700 bg-sky-50 border-sky-300' },
  started:   { label: 'Na campanha', cls: 'text-emerald-700 bg-emerald-50 border-emerald-300' },
  finished:  { label: 'Encerrado', cls: 'text-dark-400 bg-dark-700 border-dark-600' },
};

const FILTROS = [
  { id: 'candidate', label: 'Elegíveis' },
  { id: 'started',   label: 'Na campanha' },
  { id: 'pending',   label: 'Agendados' },
  { id: '',          label: 'Todos' },
];

const Badge = ({ map, s }: { map: typeof CAMP_STATUS; s: string }) => {
  const b = map[s] || { label: s || '—', cls: 'text-dark-300 bg-dark-700 border-dark-600' };
  return <span className={clsx('text-xs px-2 py-0.5 rounded border whitespace-nowrap', b.cls)}>{b.label}</span>;
};

export default function Campaigns() {
  const [mps, setMps] = useState<CampaignMarketplace[]>([]);
  const [mp, setMp] = useState('ml');
  const [camps, setCamps] = useState<Campaign[]>([]);
  const [loadingCamps, setLoadingCamps] = useState(false);
  const [erro, setErro] = useState('');

  const [sel, setSel] = useState<Campaign | null>(null);
  const [filtro, setFiltro] = useState('candidate');
  const [itens, setItens] = useState<CampaignItem[]>([]);
  const [total, setTotal] = useState<number | undefined>();
  const [cursor, setCursor] = useState('');
  const [loadingItens, setLoadingItens] = useState(false);
  const [erroItens, setErroItens] = useState('');
  const [busca, setBusca] = useState('');

  const [marcados, setMarcados] = useState<Record<string, boolean>>({});
  const [precos, setPrecos] = useState<Record<string, string>>({});
  const [resultados, setResultados] = useState<Record<string, JoinResult>>({});
  const [confirmar, setConfirmar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState('');

  const mpAtual = mps.find(m => m.id === mp);

  useEffect(() => {
    CampaignService.marketplaces().then(setMps).catch(() =>
      setMps([{ id: 'ml', name: 'Mercado Livre', available: true }]));
  }, []);

  const carregarCampanhas = async () => {
    setLoadingCamps(true); setErro(''); setSel(null); setItens([]);
    try {
      const lista = await CampaignService.list(mp);
      // Ativas e agendadas primeiro, depois por prazo de adesão
      const ordem = (s: string) => (s === 'started' ? 0 : s === 'pending' ? 1 : 2);
      lista.sort((a, b) => ordem(a.status) - ordem(b.status)
        || String(a.deadlineDate || a.endDate || '').localeCompare(String(b.deadlineDate || b.endDate || '')));
      setCamps(lista);
    } catch (e: any) { setErro(e.message); setCamps([]); }
    finally { setLoadingCamps(false); }
  };

  useEffect(() => {
    if (!mpAtual || mpAtual.available) carregarCampanhas();
    else { setCamps([]); setSel(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mp, mps.length]);

  const carregarItens = async (c: Campaign, status: string, append = false, cur = '') => {
    setLoadingItens(true); setErroItens('');
    if (!append) { setItens([]); setMarcados({}); setResultados({}); setConfirmar(false); setAviso(''); }
    try {
      const r = await CampaignService.items(c, status, cur);
      setItens(prev => (append ? [...prev, ...r.items] : r.items));
      setTotal(r.total);
      setCursor(r.nextCursor || '');
      setPrecos(prev => {
        const n = { ...prev };
        r.items.forEach(i => {
          if (n[i.itemId] === undefined) {
            const base = i.price ?? i.suggestedPrice ?? i.maxPrice;
            n[i.itemId] = base != null ? String(base) : '';
          }
        });
        return n;
      });
    } catch (e: any) { setErroItens(e.message); }
    finally { setLoadingItens(false); }
  };

  const abrir = (c: Campaign) => { setSel(c); setFiltro('candidate'); setBusca(''); carregarItens(c, 'candidate'); };

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return itens;
    return itens.filter(i => [i.title, i.qflowTitle, i.sku, i.itemId].some(v => (v || '').toLowerCase().includes(q)));
  }, [itens, busca]);

  const precoInvalido = (i: CampaignItem) => {
    if (!sel || sel.priceMode !== 'seller') return '';
    const v = Number(String(precos[i.itemId] || '').replace(',', '.'));
    if (!v || v <= 0) return 'Informe o preço';
    if (i.minPrice != null && v < i.minPrice) return `Mínimo ${brl(i.minPrice)}`;
    if (i.maxPrice != null && v > i.maxPrice) return `Máximo ${brl(i.maxPrice)}`;
    if (i.originalPrice != null && v >= i.originalPrice) return 'Precisa ser menor que o preço atual';
    return '';
  };

  const elegiveis = visiveis.filter(i => i.status === 'candidate');
  const selecionados = itens.filter(i => marcados[i.itemId] && i.status === 'candidate');
  const comErro = selecionados.filter(i => precoInvalido(i));
  const todosMarcados = elegiveis.length > 0 && elegiveis.every(i => marcados[i.itemId]);

  const inscrever = async () => {
    if (!sel || selecionados.length === 0) return;
    setEnviando(true); setAviso(''); setConfirmar(false);
    const res: Record<string, JoinResult> = {};
    let ok = 0;
    try {
      for (let k = 0; k < selecionados.length; k += 50) {
        const lote = selecionados.slice(k, k + 50).map(i => ({
          itemId: i.itemId,
          offerId: i.offerId || undefined,
          price: sel.priceMode === 'seller' ? Number(String(precos[i.itemId]).replace(',', '.')) : undefined,
        }));
        const r = await CampaignService.join(sel, lote);
        ok += r.ok;
        r.results.forEach(x => { res[x.itemId] = x; });
      }
      setResultados(prev => ({ ...prev, ...res }));
      setItens(prev => prev.map(i => (res[i.itemId]?.ok ? { ...i, status: 'pending' } : i)));
      setMarcados({});
      setAviso(`${ok} de ${selecionados.length} item(ns) inscrito(s).` +
        (ok < selecionados.length ? ' Veja o motivo nos itens marcados em vermelho.' : ''));
    } catch (e: any) { setAviso(e.message); }
    finally { setEnviando(false); }
  };

  const remover = async (i: CampaignItem) => {
    if (!sel) return;
    try {
      const r = await CampaignService.leave(sel, i);
      setResultados(prev => ({ ...prev, [i.itemId]: r }));
      if (r.ok) setItens(prev => prev.map(x => (x.itemId === i.itemId ? { ...x, status: 'candidate' } : x)));
    } catch (e: any) {
      setResultados(prev => ({ ...prev, [i.itemId]: { itemId: i.itemId, ok: false, message: e.message } }));
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark-100 mb-2 flex items-center gap-3">
            <Megaphone className="w-8 h-8 text-primary-400" /> Campanhas
          </h1>
          <p className="text-dark-400">Veja as campanhas e convites dos marketplaces e inscreva seus anúncios.</p>
        </div>
        <button
          onClick={carregarCampanhas}
          disabled={loadingCamps || (mpAtual && !mpAtual.available)}
          className="p-3 bg-dark-800 hover:bg-dark-700 text-dark-100 rounded-lg transition-colors border border-dark-600 flex items-center gap-2 shadow-lg disabled:opacity-50"
        >
          <RefreshCw className={clsx('w-5 h-5', loadingCamps && 'animate-spin')} /> Atualizar
        </button>
      </div>

      {/* MARKETPLACES */}
      <div className="flex gap-2 border-b border-dark-700">
        {mps.map(m => (
          <button
            key={m.id}
            onClick={() => setMp(m.id)}
            className={clsx('px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors',
              mp === m.id ? 'border-primary-500 text-dark-100' : 'border-transparent text-dark-400 hover:text-dark-200')}
          >
            {m.name}
            {!m.available && <span className="ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded bg-dark-700 text-dark-400">em breve</span>}
          </button>
        ))}
      </div>

      {mpAtual && !mpAtual.available ? (
        <div className="p-6 bg-dark-800 border border-dark-700 rounded-xl text-dark-300">
          <p className="font-semibold text-dark-100 mb-1">Campanhas do {mpAtual.name} ainda não estão disponíveis no QFlow.</p>
          {mpAtual.note && <p className="text-sm">{mpAtual.note}</p>}
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* LISTA DE CAMPANHAS */}
          <div className="col-span-12 lg:col-span-4 space-y-3">
            {erro && <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm">{erro}</div>}
            {loadingCamps && <div className="flex items-center gap-2 text-dark-400 text-sm"><Loader className="w-4 h-4 animate-spin" /> Carregando campanhas…</div>}
            {!loadingCamps && !erro && camps.length === 0 && (
              <div className="p-6 bg-dark-800 border border-dark-700 rounded-xl text-dark-400 text-sm">
                Nenhuma campanha disponível para esta conta no momento.
              </div>
            )}
            {camps.map(c => (
              <button
                key={`${c.type}-${c.id}`}
                onClick={() => abrir(c)}
                className={clsx('w-full text-left p-4 rounded-xl border transition-colors',
                  sel && sel.id === c.id && sel.type === c.type
                    ? 'border-primary-500 bg-primary-500/10' : 'border-dark-700 bg-dark-800 hover:border-dark-600')}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-dark-100 line-clamp-2">{c.name}</p>
                  <Badge map={CAMP_STATUS} s={c.status} />
                </div>
                <p className="text-xs text-dark-400 mb-2">{c.typeLabel}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-dark-300">
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {data(c.startDate)} → {data(c.endDate)}</span>
                  {c.deadlineDate && <span className="text-amber-700">Adesão até {data(c.deadlineDate)}</span>}
                  {c.meliPercent != null && <span className="text-emerald-700">ML banca {c.meliPercent}%</span>}
                </div>
              </button>
            ))}
          </div>

          {/* ITENS DA CAMPANHA */}
          <div className="col-span-12 lg:col-span-8">
            {!sel ? (
              <div className="p-10 bg-dark-800 border border-dark-700 rounded-xl text-center text-dark-400">
                Selecione uma campanha para ver os anúncios elegíveis.
              </div>
            ) : (
              <div className="bg-dark-800 border border-dark-700 rounded-xl">
                <div className="p-4 border-b border-dark-700 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold text-dark-100">{sel.name}</h2>
                      <p className="text-xs text-dark-400">
                        {sel.typeLabel} · {sel.priceMode === 'seller'
                          ? 'você define o preço promocional'
                          : 'o desconto é definido pelo marketplace'}
                        {total != null && ` · ${total} anúncio(s)`}
                      </p>
                    </div>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-dark-500" />
                      <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar título, SKU ou MLB"
                        className="pl-8 pr-3 py-2 text-sm bg-dark-900 border border-dark-700 rounded-lg text-dark-100 outline-none focus:ring-2 focus:ring-primary-500 w-60" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {FILTROS.map(f => (
                      <button key={f.id || 'all'}
                        onClick={() => { setFiltro(f.id); carregarItens(sel, f.id); }}
                        className={clsx('px-3 py-1 rounded-full text-xs border',
                          filtro === f.id ? 'border-primary-500 bg-primary-500/10 text-dark-100' : 'border-dark-600 text-dark-400 hover:text-dark-200')}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {erroItens && <div className="m-4 p-3 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm">{erroItens}</div>}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-dark-400 border-b border-dark-700">
                        <th className="p-3 w-8">
                          <input type="checkbox" checked={todosMarcados} disabled={elegiveis.length === 0}
                            onChange={e => setMarcados(prev => {
                              const n = { ...prev }; elegiveis.forEach(i => { n[i.itemId] = e.target.checked; }); return n;
                            })} />
                        </th>
                        <th className="p-3">Anúncio</th>
                        <th className="p-3">Preço atual</th>
                        <th className="p-3">{sel.priceMode === 'seller' ? 'Preço promocional' : 'Preço na campanha'}</th>
                        <th className="p-3">Status</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {visiveis.map(i => {
                        const inval = marcados[i.itemId] ? precoInvalido(i) : '';
                        const r = resultados[i.itemId];
                        return (
                          <tr key={i.itemId} className="border-b border-dark-700/60 align-top">
                            <td className="p-3">
                              <input type="checkbox" disabled={i.status !== 'candidate'} checked={!!marcados[i.itemId]}
                                onChange={e => setMarcados(prev => ({ ...prev, [i.itemId]: e.target.checked }))} />
                            </td>
                            <td className="p-3">
                              <div className="flex gap-3">
                                {i.thumbnail
                                  ? <img src={i.thumbnail} alt="" className="w-10 h-10 rounded object-cover bg-dark-900" />
                                  : <div className="w-10 h-10 rounded bg-dark-900" />}
                                <div className="min-w-0">
                                  <p className="text-dark-100 line-clamp-1">{i.qflowTitle || i.title || i.itemId}</p>
                                  <p className="text-xs text-dark-500 flex items-center gap-2">
                                    {i.sku && <span className="font-mono">{i.sku}</span>}
                                    {i.permalink
                                      ? <a href={i.permalink} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-0.5">{i.itemId}<ExternalLink className="w-3 h-3" /></a>
                                      : <span>{i.itemId}</span>}
                                    {i.inQflow === false && <span className="text-dark-500">· fora do QFlow</span>}
                                  </p>
                                  {r && (
                                    <p className={clsx('text-xs mt-1 flex items-center gap-1', r.ok ? 'text-emerald-700' : 'text-red-700')}>
                                      {r.ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {r.message}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-dark-200 whitespace-nowrap">{brl(i.originalPrice)}</td>
                            <td className="p-3 whitespace-nowrap">
                              {sel.priceMode === 'seller' && i.status === 'candidate' ? (
                                <div>
                                  <input type="number" step="0.01" min="0" value={precos[i.itemId] ?? ''}
                                    onChange={e => setPrecos(prev => ({ ...prev, [i.itemId]: e.target.value }))}
                                    className={clsx('w-28 px-2 py-1 bg-dark-900 border rounded text-dark-100 outline-none',
                                      inval ? 'border-red-500' : 'border-dark-700 focus:border-primary-500')} />
                                  <p className={clsx('text-[11px] mt-0.5', inval ? 'text-red-700' : 'text-dark-500')}>
                                    {inval || ((i.minPrice != null || i.maxPrice != null)
                                      ? `${brl(i.minPrice)} – ${brl(i.maxPrice)}`
                                      : i.suggestedPrice != null ? `Sugerido ${brl(i.suggestedPrice)}` : '')}
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <span className="text-dark-100">{brl(i.price ?? i.suggestedPrice)}</span>
                                  {i.meliPercent != null && <p className="text-[11px] text-emerald-700">ML banca {i.meliPercent}%</p>}
                                </div>
                              )}
                            </td>
                            <td className="p-3"><Badge map={ITEM_STATUS} s={i.status} /></td>
                            <td className="p-3 text-right">
                              {(i.status === 'started' || i.status === 'pending') && (
                                <button onClick={() => remover(i)} className="text-xs text-red-700 hover:text-red-800">Remover</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {loadingItens && <div className="p-4 flex items-center gap-2 text-dark-400 text-sm"><Loader className="w-4 h-4 animate-spin" /> Carregando anúncios…</div>}
                {!loadingItens && !erroItens && visiveis.length === 0 && (
                  <div className="p-6 text-center text-dark-400 text-sm">Nenhum anúncio neste filtro.</div>
                )}
                {cursor && !loadingItens && (
                  <div className="p-3 text-center">
                    <button onClick={() => carregarItens(sel, filtro, true, cursor)} className="text-sm text-primary-400 hover:text-primary-300">Carregar mais</button>
                  </div>
                )}

                {/* AÇÕES */}
                <div className="p-4 border-t border-dark-700 flex items-center justify-between gap-4">
                  <div className="text-sm">
                    {aviso && <span className="text-dark-200">{aviso}</span>}
                    {!aviso && comErro.length > 0 && (
                      <span className="text-red-700 inline-flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" /> Corrija o preço de {comErro.length} item(ns) antes de inscrever.
                      </span>
                    )}
                  </div>
                  {confirmar ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-dark-200">Inscrever {selecionados.length} anúncio(s) em "{sel.name}"?</span>
                      <button onClick={() => setConfirmar(false)} className="px-3 py-2 text-sm rounded-lg border border-dark-600 text-dark-300 hover:bg-dark-700">Cancelar</button>
                      <button onClick={inscrever} className="px-3 py-2 text-sm rounded-lg bg-primary-600 hover:bg-primary-500 text-white">Confirmar</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmar(true)}
                      disabled={enviando || selecionados.length === 0 || comErro.length > 0}
                      className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2"
                    >
                      {enviando && <Loader className="w-4 h-4 animate-spin" />}
                      Inscrever selecionados ({selecionados.length})
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
